/* ═══════════════════════════════════════════════════════════════
   TaskAttachments — Task Sheet's Attachments section. Read (+ download)
   for anyone who can see the task; add/remove gated on the task's own
   edit right (Decision 12).
   ═══════════════════════════════════════════════════════════════ */
import type { TaskAttachment } from '../../../../shared/types';
import { Button } from '../../../components/ui/button';
import { Icon } from '../../../components/ui/Icon';
import EmptyState from '../../../components/EmptyState';
import { rem } from '../../../lib/rem';
import { X, Paperclip } from 'lucide-react';

export interface TaskAttachmentsProps {
  attachments: TaskAttachment[];
  canEdit: boolean;
  onAdd: () => Promise<void>;
  onDelete: (id: string) => Promise<void>;
}

const sectionHeaderStyle = {
  fontSize: 'var(--font-size-xs)',
  fontWeight: 700,
  color: 'var(--tm)',
  textTransform: 'uppercase' as const,
  letterSpacing: '.05em',
};

function downloadAttachment(att: TaskAttachment) {
  const byteChars = atob(att.data);
  const byteNumbers = new Array(byteChars.length);
  for (let i = 0; i < byteChars.length; i++) byteNumbers[i] = byteChars.charCodeAt(i);
  const blob = new Blob([new Uint8Array(byteNumbers)], {
    type: att.mimeType || 'application/octet-stream',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = att.fileName;
  a.click();
  URL.revokeObjectURL(url);
}

export function TaskAttachments({ attachments, canEdit, onAdd, onDelete }: TaskAttachmentsProps) {
  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: rem(6),
        }}
      >
        <span style={sectionHeaderStyle}>
          Attachments {attachments.length > 0 ? `(${attachments.length})` : ''}
        </span>
        {canEdit && (
          <Button variant="ghost" size="sm" onClick={() => void onAdd()}>
            + Add
          </Button>
        )}
      </div>

      {attachments.length === 0 && (
        <EmptyState compact icon={<Icon icon={Paperclip} size="md" />} title="No attachments" />
      )}

      {attachments.map((att) => (
        <div
          key={att.id}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
            padding: `var(--space-1) 0`,
            borderBottom: '1px solid var(--bd)',
          }}
        >
          <span
            style={{
              fontSize: 'var(--font-size-base)',
              color: 'var(--tx)',
              flex: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {att.fileName}
          </span>
          <Button variant="ghost" size="sm" onClick={() => downloadAttachment(att)}>
            Download
          </Button>
          {canEdit && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onDelete(att.id)}
              aria-label="Remove attachment"
            >
              <Icon icon={X} size="md" />
            </Button>
          )}
        </div>
      ))}
    </div>
  );
}
